"use client";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import uuid4 from "uuid4";
import { styled } from "@mui/material/styles";
import {
  Grid,
  Card,
  Dialog,
  CardContent,
  Typography,
  Box,
  DialogContent,
  DialogTitle,
  IconButton,
  SvgIcon,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-light-svg-icons";

const teamMembers = [
  {
    name: 'Daniël "The Code Alchemist" Rutten',
    image: "/images/konami/daniel.png",
    description:
      "Hailing from the sunlit shores of Hawke's Bay, New Zealand, Daniël has been wielding the power of code since 1985, mastering the ancient arts of software creation. Known as The Code Alchemist, he specializes in taming Knowledge Management Systems and commanding relational databases, optimizing workflows with a flick of his digital wand. When he’s not in the lab engineering brilliant solutions, you’ll find him rowing, scaling mountains, or decrypting the mysteries of cryptography and AI. Fueled by coffee, Daniël is always ready for his next adventure—pushing the boundaries of innovation and tech wizardry!",
  },
  {
    name: 'Veena "The Code Conductor" Chandamalla',
    image: "/images/konami/veena.png",
    description:
      "A master of both front-end finesse and back-end brilliance, Veena—The Code Conductor—orchestrates seamless software symphonies as a dynamic Full-stack .NET Developer. With a focus on delivering top-quality solutions, she bridges the gap between design and logic, ensuring every line of code works in perfect harmony. Outside of the digital world, Veena taps into her creative side, experimenting with new recipes in her kitchen laboratory and enjoying time with her little sidekick. Balancing code and family life, she’s always cooking up something extraordinary—both on and off the screen!",
  },
  {
    name: 'Constantin "The Microservice Guardian" Cabiniuc',
    image: "/images/konami/constantin.png",
    description:
      "From the depths of server-side logic emerges Constantin—The Microservice Guardian—, a Software Developer who brings structure, stability, and scalability to complex platforms. Working primarily within the .NET ecosystem, he crafts resilient microservices and finely tuned SQL Server solutions that form the unseen foundation of seamless digital experiences. His curiosity spans the entire computing universe—from web development to IoT, embedded systems, and hardware. Beyond the code, he’s endlessly passionate about the art of photography, vintage computers, and motorcycle travel, and can often be found sharing stories and ideas over a great cup of coffee.",
  },
  {
    name: 'Davide "The Interface Maestro" Girotto',
    image: "/images/konami/davide.png",
    description:
      "Davide, known as The Interface Maestro, is a Senior UX Engineer and Front-End Developer with the power to craft digital experiences that blend form and function effortlessly. With his eye for design and technical skills, Davide builds interfaces that are not only powerful but intuitive, ensuring each product feels natural to use. When he’s not sculpting the digital realm, Davide explores the fusion of art and technology through music production and DJing. Off the decks, he’s often experimenting in the kitchen or baking up new creations. Whether in code or sound, his creativity always hits the right notes.",
  },
  {
    name: 'Irin "The UI/UX Enchantress" Sajan',
    image: "/images/konami/irin.png",
    description:
      "With the power to weave code into seamless, intuitive experiences, Irin—The UI/UX Enchantress—is a Front-End Developer dedicated to turning complex user interfaces into elegant digital solutions. Her sharp design sense and expertise in modern JavaScript frameworks allow her to simplify even the toughest UI challenges with ease. Outside of her coding magic, Irin enjoys reading and taking walks in nature with her family, finding peace and inspiration in the world around her. Whether in the digital realm or the natural one, she’s always creating something beautiful.",
  },
];

const quotes = [
  "With great power comes great responsibility... to write clean code.",
  "In a world full of bugs, be a debugger.",
  "Heroes don't just code; they refactor!",
  "Every superhero has their tools; mine are JavaScript and a great debugger.",
  "Not all heroes wear capes, some write functions and squash bugs.",
  "Just like Iron Man's suit, the best code is a blend of power and precision.",
  "In the battle against bugs, persistence is your superpower.",
  "Code is like a superpower: in the wrong hands, it’s chaos; in the right hands, it’s magic.",
  "Behind every powerful algorithm, there’s a hero ready to optimize.",
  "The best heroes don’t fly, they debug in real time.",
];

const getRandomQuote = () => {
  const randomIndex = Math.floor(Math.random() * quotes.length);
  return quotes[randomIndex];
};

const ImageContainer = styled("div")({
  position: "relative",
  width: "100%",
  height: "150px",
  overflow: "visible",
  backgroundColor: "#f5f5f5",
});

const Image = styled("img")({
  position: "absolute",
  top: "-80px",
  left: "50%",
  transform: "translateX(-50%)",
  width: "200px",
  height: "200px",
});

export const KonamiModal = (props) => {
  const { onClose, open = false } = props;
  const [quote, setQuote] = useState("");

  useEffect(() => {
    setQuote(getRandomQuote());
  }, []);

  return (
    <Dialog onClose={onClose} open={open} maxWidth="xl" fullWidth>
      <DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 15,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <SvgIcon>
            <FontAwesomeIcon icon={faXmark} />
          </SvgIcon>
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: "center", my: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Meet Our Team
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            The Super Squad Behind the Code
          </Typography>
          <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
            &quot;{quote}&quot;
          </Typography>
        </Box>
        <Grid container spacing={4} sx={{ my: 8 }}>
          {teamMembers.map((member, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }} key={uuid4()}>
              <Card
                sx={{
                  boxShadow: 2,
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: 5,
                  },
                  overflow: "visible",
                }}
              >
                <ImageContainer>
                  <Image src={member.image} alt={member.name} />
                </ImageContainer>
                <CardContent sx={{ height: { md: 450, lg: 580 } }}>
                  <Typography variant="h6" component="h2">
                    {member.name}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    {member.role}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {member.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

KonamiModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
};
